import { useRef, useState } from "react";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Upload, ScanLine, Archive, AlertTriangle, Trash2, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useShipmentSession, useImportNajave, useArchiveShipmentSession,
  useDeleteShipmentSession, useRemoveExtraItem, lookupShipmentOrder, type OrderLookup,
} from "@/hooks/use-shipment-counting";
import { toast } from "sonner";

const STATUS_LABELS: Record<string, string> = {
  pending: "Nije skeniran",
  scanned: "Skeniran",
  extra: "Višak",
};

// bez dijakritike i velikih slova, da "Cengic" nađe "Čengić"
function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\u0111/g, "d");
}

export function ShipmentSessionDetailPage() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const { data: session, isLoading } = useShipmentSession(sessionId);
  const importNajave = useImportNajave();
  const archiveSession = useArchiveShipmentSession();
  const deleteSession = useDeleteShipmentSession();
  const removeExtra = useRemoveExtraItem(sessionId);
  const navigate = useNavigate();

  const fileRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "extra" | "scanned">("all");
  const [lookup, setLookup] = useState<{ tn: string; recipient: string; result: OrderLookup | "loading" } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [query, setQuery] = useState("");

  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Učitavanje…</div>;
  if (!session) return <div className="p-4 text-sm text-destructive">Sesija nije pronađena.</div>;

  const archived = session.status === "archived";
  const { total, scanned, missing, extra } = session.progress;
  const uredno = total > 0 && missing === 0 && extra === 0;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    try {
      const { results } = await importNajave.mutateAsync({ id: sessionId, files: Array.from(files) });
      for (const r of results) {
        if (!r.ok) toast.error(`${r.fileName}: ${r.error}`);
        else if (r.warning) toast.warning(`${r.fileName}: ${r.warning}`);
        else toast.success(`${r.fileName}: uvezeno`);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function openLookup(item: any) {
    setLookup({ tn: item.trackingNumber, recipient: item.recipient, result: "loading" });
    try {
      const result = await lookupShipmentOrder(item.trackingNumber);
      setLookup({ tn: item.trackingNumber, recipient: item.recipient, result });
    } catch (err: any) {
      toast.error(err?.message || "Greška pri traženju narudžbe");
      setLookup(null);
    }
  }

  async function handleArchive() {
    try {
      await archiveSession.mutateAsync(sessionId);
      toast.success("Sesija arhivirana");
      setConfirmArchive(false);
    } catch (err: any) {
      toast.error(err.message);
    }
  }

  // jedno polje traži i po imenu primaoca i po broju pošiljke — bez prebacivanja moda
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const items = (session.items as any[])
    .filter((i) => (filter === "all" ? true : i.status === filter))
    .filter((i) => {
      if (terms.length === 0) return true;
      const tn = String(i.trackingNumber ?? "");
      const haystack = `${normalize(String(i.recipient ?? ""))} ${normalize(tn)} ${tn.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`;
      return terms.every((t) => haystack.includes(t));
    });

  return (
    <div>
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/warehouse/brojanje-posiljki" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-lg font-semibold">{session.name}</h1>
          {archived && <Badge variant="secondary">Arhivirana</Badge>}
          {uredno && <Badge className="bg-green-600 text-white">Sve uredno</Badge>}
          <div className="ml-auto flex gap-2">
            {!archived && (
              <>
                <input
                  ref={fileRef} type="file" accept="application/pdf" multiple hidden
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importNajave.isPending}>
                  <Upload className="h-4 w-4 mr-1" />
                  {importNajave.isPending ? "Uvoz…" : "Dodaj najave"}
                </Button>
                {total === 0 ? (
                  <Button size="sm" disabled>
                    <ScanLine className="h-4 w-4 mr-1" /> Počni brojanje
                  </Button>
                ) : (
                  <Button size="sm" asChild>
                    <Link to="/warehouse/brojanje-posiljki/$sessionId/count" params={{ sessionId }}>
                      <ScanLine className="h-4 w-4 mr-1" /> Počni brojanje
                    </Link>
                  </Button>
                )}
                <Button
                  variant="outline" size="sm"
                  onClick={() => (uredno ? handleArchive() : setConfirmArchive(true))}
                  disabled={archiveSession.isPending || total === 0}
                >
                  <Archive className="h-4 w-4 mr-1" /> Arhiviraj
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={async () => {
                    if (!confirm("Obrisati sesiju i sve stavke?")) return;
                    try {
                      await deleteSession.mutateAsync(sessionId);
                      navigate({ to: "/warehouse/brojanje-posiljki" });
                    } catch (err: any) {
                      toast.error(err.message);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 text-center">
          {[
            ["Ukupno", total, ""],
            ["Skenirano", scanned, "text-green-600"],
            ["Fali", missing, missing > 0 ? "text-destructive" : ""],
            ["Višak", extra, extra > 0 ? "text-amber-600" : ""],
          ].map(([label, value, cls]) => (
            <div key={label as string} className="border rounded-lg p-3">
              <div className={`text-xl font-semibold ${cls}`}>{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>

        {session.manifests.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-sm font-medium">Najave ({session.manifests.length})</h2>
            {session.manifests.map((m: any) => (
              <div key={m.id} className="border rounded-lg p-3 flex items-center gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">[{m.firmCode}] {m.firmName}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {m.firmAddress} {m.announcedAt && `· ${m.announcedAt}`} · {m.fileName}
                  </div>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <div>{m.scannedCount} / {m.expectedCount}</div>
                  {m.parseWarning && (
                    <div className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> {m.parseWarning}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pretraži po imenu i prezimenu ili broju pošiljke…"
            className="pl-9 pr-9"
            autoComplete="off"
          />
          {query && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setQuery("")}
              title="Očisti pretragu"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
          <TabsList>
            <TabsTrigger value="all">Sve ({session.items.length})</TabsTrigger>
            <TabsTrigger value="pending">Fali ({missing})</TabsTrigger>
            <TabsTrigger value="extra">Višak ({extra})</TabsTrigger>
            <TabsTrigger value="scanned">Skenirano ({scanned})</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="border rounded-lg divide-y">
          {items.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">
              {terms.length > 0 ? "Nema rezultata za pretragu." : "Nema stavki."}
            </p>
          )}
          {items.map((i: any) => (
            <div key={i.id} className="p-2 px-3 flex items-center gap-3 text-sm">
              <button className="font-mono text-primary hover:underline" onClick={() => openLookup(i)}>
                {i.trackingNumber}
              </button>
              <span className="text-muted-foreground truncate">{i.recipient}</span>
              <span className="ml-auto shrink-0 flex items-center gap-2">
                {i.codAmount != null && <span className="text-xs">{i.codAmount.toFixed(2)} KM</span>}
                <Badge
                  variant={i.status === "scanned" ? "default" : i.status === "extra" ? "destructive" : "secondary"}
                >
                  {STATUS_LABELS[i.status]}
                </Badge>
                {i.status === "extra" && !archived && (
                  <button
                    className="text-muted-foreground hover:text-destructive"
                    title="Ukloni višak sa liste"
                    onClick={async () => {
                      try {
                        await removeExtra.mutateAsync(i.id);
                        toast.success(`${i.trackingNumber} uklonjen sa liste`);
                      } catch (err: any) {
                        toast.error(err.message || "Greška pri uklanjanju");
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!lookup} onOpenChange={(o) => !o && setLookup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-mono">{lookup?.tn}</DialogTitle>
          </DialogHeader>
          {lookup?.result === "loading" && <p className="text-sm">Tražim narudžbu…</p>}
          {lookup && lookup.result !== "loading" && lookup.result && (
            <div className="space-y-1 text-sm">
              <p><span className="text-muted-foreground">Narudžba:</span> #{lookup.result.number ?? lookup.result.orderId}</p>
              {lookup.result.customerName && <p><span className="text-muted-foreground">Kupac:</span> {lookup.result.customerName}</p>}
              {lookup.result.total != null && <p><span className="text-muted-foreground">Iznos:</span> {lookup.result.total.toFixed(2)} KM</p>}
              {lookup.result.status && <p><span className="text-muted-foreground">Status:</span> {lookup.result.status}</p>}
              <Button size="sm" className="mt-2" asChild>
                <Link to="/orders/$orderId" params={{ orderId: lookup.result.orderId }}>
                  Otvori narudžbu
                </Link>
              </Button>
            </div>
          )}
          {lookup && lookup.result !== "loading" && !lookup.result && (
            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground">Nije iz ovog sistema.</p>
              {lookup.recipient && <p><span className="text-muted-foreground">Primalac iz najave:</span> {lookup.recipient}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arhivirati s odstupanjima?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            {missing > 0 && `Fali ${missing} pošiljki. `}
            {extra > 0 && `Višak: ${extra}. `}
            Sesija će trajno zapamtiti ova odstupanja.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmArchive(false)}>Odustani</Button>
            <Button onClick={handleArchive} disabled={archiveSession.isPending}>Arhiviraj svejedno</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
